import React, { useState, useRef, useEffect } from "react"
import { useVirtualizer } from "@tanstack/react-virtual"
import { useAsyncQueuer } from "@tanstack/react-pacer"
import { LeadSaveManager, createLeadList } from "./lead-save-manager"
import { Button, ButtonSuffix } from "~/react/ui/button"
import { ButtonGroup } from "~/react/ui/button-group"
import { CurrencyHelper } from "~/helpers/currency_helper"
import { useEmailSearch } from "~/react/hooks/useEmailSearch"
import { VirtualizedResults, ESTIMATED_RESULT_SIZE } from "~/react/components/multi_domain_search/virtualized-results"
import { Alert, AlertContent, AlertIcon, AlertDescription } from "~/react/ui/alert"
import { Snackbar, SnackbarText, SnackbarLink } from "~/react/ui/snackbar"
import { Icon } from "~/react/ui/icon"
import { LoadingSpinner } from "~/react/ui/loading-spinner"
import { Tag, TagIcon, TagLabel } from "~/react/ui/tag"
import { LookalikesFilters, LookalikesFiltersType } from "./lead_lookalikes/filters"
import { Department, VerificationStatus } from "./filters/shared"
import { LocationsMappingsType, filterFunction } from "./search/filters"
import { ResultType } from "./multi_domain_search/result"
import {
  search_lookalikes_path,
  multi_domain_search_emails_search_index_path,
  multi_domain_search_leads_search_index_path,
  api_lead_path,
  leads_path,
  mappings_locations_path,
} from "~/lib/routes"
import { useLocationMappings } from "../hooks/useLocationMappings"
import { LoadingShimmer } from "../ui/loading-shimmer"
import notEnoughMatchesIllustration from "~/images/not-enough-matches.svg"

interface LeadLookalikesEmptyStateProps {
  errorTitle?: string | null
  errorDescription?: string | null
  embeddedMode?: boolean
  onClose?: () => void
}

// TODO: Extract this into a global EmptyState component.
// Note: There's a WIP on another branch for this, hence not extracting it here.
const LeadLookalikesEmptyState: React.FC<LeadLookalikesEmptyStateProps> = ({
  errorTitle,
  errorDescription,
  embeddedMode = false,
  onClose,
}) => {
  return (
    <div
      className={
        embeddedMode
          ? "empty-state lead-lookalikes__empty-state"
          : "empty-state empty-state--sidepanel lead-lookalikes__empty-state"
      }
    >
      <div className="empty-state__img">
        <img src={notEnoughMatchesIllustration} alt="" />
      </div>
      <div className="empty-state__content">
        <h3 className="empty-state__title">{errorTitle}</h3>
        {errorDescription && <p className="empty-state__description">{errorDescription}</p>}
        {!embeddedMode && onClose && (
          <div className="empty-state__actions">
            <Button onClick={onClose}>{i18n.t("js.lead_lookalikes.empty_state.button")}</Button>
          </div>
        )}
      </div>
    </div>
  )
}

interface LeadLookalikesProps {
  leadIds: number[]
  searchFilters?: Record<string, any>
  user: any
  leadsLists: { id: number; name: string }[]
  numberOfCreditsLeft: number
  similarityThresholds: {
    medium: number
    high: number
  }
  maxLookalikesLimit: number
  embeddedMode?: boolean
  leadName?: string
  onClose?: () => void
}

const LeadLookalikes: React.FC<LeadLookalikesProps> = ({
  leadIds,
  searchFilters = {},
  user,
  leadsLists: initialLeadsLists,
  numberOfCreditsLeft,
  similarityThresholds,
  maxLookalikesLimit,
  embeddedMode = false,
  leadName,
  onClose,
}) => {
  // State for email IDs and similarity scores
  const [emailIds, setEmailIds] = useState<number[]>([])
  const [similarityScores, setSimilarityScores] = useState<Record<string, number>>({})
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<{
    title: string
    description: string
  } | null>(null)
  const [leadsLists, setLeadsLists] = useState<{ id: number; name: string }[]>(initialLeadsLists)
  const [intents, setIntents] = useState<
    Array<{
      metadata: {
        position?: string | null
        industry?: string | null
        country?: string | null
        signals?: string[] | null
      }
      lookalikes: Array<{
        email_id: number
        similarity_score: number
        distance: number
        intent_index: number
      }>
    }>
  >([])
  const [selectedIntentIndex, setSelectedIntentIndex] = useState<number | null>(null)
  const [sourceKeywords, setSourceKeywords] = useState<string[]>([])
  const [lookalikeKeywords, setLookalikeKeywords] = useState<Record<string, string[]>>({})
  const [selectedKeywords, setSelectedKeywords] = useState<string[]>([])
  const [domains, setDomains] = useState<any[]>([])

  // Use shared hook for email search functionality
  const {
    results: emails,
    setResults: setEmails,
    revealingIds,
    revealedIds,
    selectedIds,
    handleReveal,
    handleRevealMultiple,
    handleSelect,
  } = useEmailSearch({
    initialEmails: [],
    context: "lookalikes",
    source: "lookalikes",
  })

  // Local state for filters
  const [filters, setFilters] = useState<LookalikesFiltersType>({
    jobTitles: [],
    verificationStatus: [],
    departments: [],
    similarityRange: [40, 100],
    requiredFields: [],
    location: [],
    hideAlreadySaved: true,
  })
  const locationsMappings = useLocationMappings()

  const parentRef = useRef<HTMLDivElement>(null)
  const [contentHeight, setContentHeight] = useState<string>("auto")

  // Calculate content height dynamically to fill space between content top and footer
  const calculateContentHeight = React.useCallback(() => {
    if (!parentRef.current) return

    const windowHeight = window.innerHeight
    const contentTop = parentRef.current.getBoundingClientRect().top
    const footerElement = document.querySelector(".sidepanel__footer") as HTMLElement
    const footerHeight = footerElement ? footerElement.offsetHeight : 0

    setContentHeight(`${Math.max(windowHeight - contentTop - footerHeight - 2, 200)}px`)
  }, [])

  // Recalculate height on mount, resize, and when filters/data change
  React.useEffect(() => {
    calculateContentHeight()

    const handleResize = () => calculateContentHeight()
    window.addEventListener("resize", handleResize)

    const timeoutId = setTimeout(calculateContentHeight, 100)

    return () => {
      window.removeEventListener("resize", handleResize)
      clearTimeout(timeoutId)
    }
  }, [calculateContentHeight, filters, emails])

  const clearFilters = () => {
    setFilters({
      requiredFields: [],
      departments: [],
      jobTitles: [],
      verificationStatus: [],
      location: [],
      similarityRange: [40, 100],
      hideAlreadySaved: false,
    })
    setSelectedKeywords([])
  }

  const anyFiltersSet =
    filters.departments.length > 0 ||
    filters.jobTitles.length > 0 ||
    filters.requiredFields.length > 0 ||
    filters.verificationStatus.length > 0 ||
    filters.location.length > 0 ||
    filters.hideAlreadySaved ||
    (filters.similarityRange && (filters.similarityRange[0] !== 40 || filters.similarityRange[1] !== 100)) ||
    (selectedKeywords.length > 0 && selectedKeywords.length < sourceKeywords.length)

  // Fetch email details on demand using MDS emails endpoint - defined before queue
  const fetchEmailDetails = async (ids: number[], scores: Record<string, number>) => {
    try {
      const response = await fetch(multi_domain_search_emails_search_index_path(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content,
        },
        body: JSON.stringify({
          email_ids: ids,
          similarity_scores: scores,
          include_domains: true,
        }),
      })

      if (!response.ok) {
        console.error(`Failed to fetch email details: ${response.status} ${response.statusText}`)
        return
      }

      const data = await response.json()

      if (!data.emails || !Array.isArray(data.emails)) {
        console.error("Invalid response format: expected emails array")
        return
      }

      setEmails((prev) => {
        const existingIds = new Set(prev.map((email) => email.id))
        const newEmails = data.emails.filter((email) => !existingIds.has(email.id))
        return [...prev, ...newEmails]
      })

      // Store domains for company popover
      if (data.domains) {
        setDomains((prev) => {
          const existingValues = new Set(prev.map((d) => d.value))
          const newDomains = data.domains.filter((d: any) => !existingValues.has(d.value))
          return [...prev, ...newDomains]
        })
      }
    } catch (error) {
      console.error("Error fetching email details:", error)
    }
  }

  const queue = useAsyncQueuer(
    async (batch: { ids: number[]; scores: Record<string, number> }) => {
      await fetchEmailDetails(batch.ids, batch.scores)
    },
    { concurrency: 10, wait: 10 }, // Match MDS concurrency settings
  )

  const fetchLookalikes = async () => {
    setIsLoading(true)
    setError(null)
    const batchSize = 10000

    const requestBody: any = {}

    if (leadIds && leadIds.length > 0) {
      requestBody.lead_ids = leadIds
    } else if (searchFilters && Object.keys(searchFilters).length > 0) {
      // Use filters passed from server
      Object.entries(searchFilters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          requestBody[key] = value
        }
      })
    } else {
      // Fallback to URL params (for backwards compatibility)
      const urlParams = new URLSearchParams(window.location.search)
      urlParams.forEach((value, key) => {
        // Exclude parameters that shouldn't be sent to the search endpoint
        if (
          key !== "offset" &&
          key !== "controller" &&
          key !== "action" &&
          key !== "lookalike" &&
          !key.startsWith("lookalike[")
        ) {
          requestBody[key] = value
        }
      })
    }

    try {
      const response = await fetch(search_lookalikes_path(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content,
        },
        body: JSON.stringify(requestBody),
      })
      const data = await response.json()

      // Handle error response from API
      if (data.error) {
        const errorKey = `js.lead_lookalikes.errors.${data.error}`
        const title = i18n.t(`${errorKey}.title`, {
          defaultValue: i18n.t("js.lead_lookalikes.errors.generic.title"),
        })
        const description = i18n.t(`${errorKey}.description`, {
          defaultValue: i18n.t("js.lead_lookalikes.errors.generic.description"),
        })
        setError({ title, description })
        setIntents([])
        setEmailIds([])
        setEmails([])
        return
      }

      // Handle intent-based response (both single and multi-intent)
      if (data.intents && data.intents.length > 0) {
        // Transform backend structure: data.intents[].intent -> metadata
        const transformedIntents = data.intents.map((item: any) => ({
          metadata: item.intent, // Backend sends 'intent', we use 'metadata' in frontend
          lookalikes: item.lookalikes,
        }))

        // Flatten all lookalikes from all intents, deduplicate by email_id (keep highest score),
        // and sort by similarity score descending
        const lookalikeMap = new Map<number, any>()
        transformedIntents.forEach((intent: any) => {
          intent.lookalikes.forEach((lookalike: any) => {
            const existing = lookalikeMap.get(lookalike.email_id)
            // Keep the lookalike with the highest similarity score
            if (!existing || lookalike.similarity_score > existing.similarity_score) {
              lookalikeMap.set(lookalike.email_id, lookalike)
            }
          })
        })

        // Sort all lookalikes by similarity score descending
        const allLookalikes = Array.from(lookalikeMap.values()).sort((a, b) => b.similarity_score - a.similarity_score)

        const newEmailIds = allLookalikes.map((l) => l.email_id)
        const newScores: Record<string, number> = {}
        allLookalikes.forEach((l) => {
          newScores[l.email_id] = l.similarity_score
        })

        // Only set intents if we have actual lookalikes
        if (newEmailIds.length > 0) {
          setIntents(transformedIntents)
        } else {
          setIntents([])
        }
        setEmailIds(newEmailIds)
        setSimilarityScores(newScores)

        // Queue email detail fetching
        for (let i = 0; i < newEmailIds.length; i += batchSize) {
          const batchIds = newEmailIds.slice(i, i + batchSize)
          const batchScores = {}
          batchIds.forEach((id) => {
            if (newScores[id]) {
              batchScores[id] = newScores[id]
            }
          })
          queue.addItem({ ids: batchIds, scores: batchScores })
        }
        return
      }

      // Fallback: Standard database response without intents
      const newEmailIds = data.email_ids || []
      const newScores = data.similarity_scores || {}

      setEmailIds(newEmailIds)
      setSimilarityScores(newScores)
      setIntents([])

      // Store keywords data for filtering
      if (data.source_keywords) {
        setSourceKeywords(data.source_keywords)
      }
      if (data.lookalike_keywords) {
        setLookalikeKeywords(data.lookalike_keywords)
      }

      // Queue email detail fetching
      for (let i = 0; i < newEmailIds.length; i += batchSize) {
        const batchIds = newEmailIds.slice(i, i + batchSize)
        const batchScores = {}
        batchIds.forEach((id) => {
          if (newScores[id]) {
            batchScores[id] = newScores[id]
          }
        })
        queue.addItem({ ids: batchIds, scores: batchScores })
      }
    } catch (error) {
      console.error("Error fetching lookalikes:", error)
      setError({
        title: i18n.t("js.lead_lookalikes.errors.generic.title"),
        description: i18n.t("js.lead_lookalikes.errors.generic.description"),
      })
      setIntents([])
      setEmailIds([])
      setEmails([])
    } finally {
      setIsLoading(false)
    }
  }

  // Initial fetch on mount
  React.useEffect(() => {
    fetchLookalikes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run once on mount

  // Initialize selectedKeywords with all sourceKeywords when they change
  React.useEffect(() => {
    if (sourceKeywords.length > 0) {
      setSelectedKeywords(sourceKeywords)
    }
  }, [sourceKeywords])

  // Get filtered email IDs based on intent selection
  const getFilteredEmailIds = React.useMemo(() => {
    // If a specific intent is selected, filter to only that intent's lookalikes
    if (intents.length > 1 && selectedIntentIndex !== null) {
      const selectedIntent = intents[selectedIntentIndex]
      if (selectedIntent) {
        return new Set(selectedIntent.lookalikes.map((l) => l.email_id))
      }
    }
    // Otherwise, return all email IDs (show all intents combined or single intent)
    return null
  }, [selectedIntentIndex, intents])

  // Filter emails based on filters and similarity score, then sort by emailIds order
  const filteredEmails = React.useMemo(() => {
    // First, filter by intent if applicable
    let intentFilteredEmails = emails
    if (getFilteredEmailIds !== null) {
      intentFilteredEmails = emails.filter((email) => getFilteredEmailIds.has(email.id))
    }

    // Transform emails to ResultType for filterFunction compatibility
    const emailsAsResults: ResultType[] = intentFilteredEmails.map((email) => ({
      id: email.id,
      email: email.email,
      jobTitle: email.jobTitle,
      phoneNumberExists: !!email.phone_number,
      fullNameExists: !!(email.first_name && email.last_name),
      revealed: email.revealed || false,
      lead: email.lead ?? null, // normalize undefined to null so hideAlreadySaved logic works
      department: email.department,
      decisionMaker: false, // Not applicable for lookalikes
      country: email.country,
      state: email.state,
      city: email.city,
      verification: email.verification,
      name: email.name,
      companyLogo: email.company_logo,
      companyName: email.company_name,
      domain: email.domain,
    }))

    let keywordFiltered = emailsAsResults
    if (selectedKeywords.length > 0) {
      keywordFiltered = emailsAsResults.filter((result) => {
        const keywords = lookalikeKeywords[result.id.toString()] || []
        return keywords.some((kw) => selectedKeywords.includes(kw))
      })
    }

    // Filter by similarity score (specific to lookalikes)
    const similarityRange = filters.similarityRange || [40, 100]
    const similarityFiltered = keywordFiltered.filter((result) => {
      const email = emails.find((e) => e.id === result.id)
      if (email?.similarity_score) {
        return email.similarity_score >= similarityRange[0] && email.similarity_score <= similarityRange[1]
      }
      return true
    })

    const filtered = filterFunction(similarityFiltered, filters, locationsMappings, revealedIds)

    // Convert back to email objects
    const filteredEmailIds = new Set(filtered.map((r) => r.id))
    const filteredEmailObjects = emails.filter((email) => filteredEmailIds.has(email.id))

    // Sort by the order in emailIds array to preserve similarity score ordering
    const emailIdToIndex = new Map(emailIds.map((id, index) => [id, index]))
    return filteredEmailObjects.sort((a, b) => {
      const indexA = emailIdToIndex.get(a.id) ?? Infinity
      const indexB = emailIdToIndex.get(b.id) ?? Infinity
      return indexA - indexB
    })
  }, [
    emails,
    filters,
    emailIds,
    locationsMappings,
    revealedIds,
    selectedKeywords,
    sourceKeywords,
    lookalikeKeywords,
    getFilteredEmailIds,
  ])

  // Virtual scrolling - reusing same pattern as multi-domain search
  const virtualizer = useVirtualizer({
    count: filteredEmails.length,
    getScrollElement: () => parentRef.current,
    estimateSize: React.useCallback(() => ESTIMATED_RESULT_SIZE, []),
    overscan: 5,
  })

  // Handles saving a lead to a list - exact same logic as multi_domain_search
  const handleLeadsListSave = async (name: string, listId: number, emailId: number) => {
    let list = { id: listId, name }
    if (listId === null) {
      // Create a new list
      list = await createLeadList(name)
      if (list) {
        setLeadsLists((prev) => [...prev, list])
      } else {
        return
      }
    }

    const result = emails.find((result) => result.id === emailId)
    const lead = result?.lead

    if (!lead) {
      // Create a new lead
      const response = await fetch(multi_domain_search_leads_search_index_path(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content,
        },
        body: JSON.stringify({
          email_id: emailId,
          leads_list_id: list.id,
        }),
      })
      const json = await response.json()

      if (response.ok) {
        setEmails((prev) => prev.map((result) => (result.id === emailId ? { ...result, lead: json.data } : result)))
      } else {
        App.displayFlash("error", "Failed to save lead to a list")
      }
    } else {
      const response = await fetch(api_lead_path(lead.id, { api_version: "v2", api_key: gon.api_key }), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          leads_list_ids: [...lead.leads_list_ids, list.id],
        }),
      })

      if (response.ok) {
        setEmails((prev) =>
          prev.map((result) =>
            result.id === emailId
              ? {
                  ...result,
                  lead: {
                    ...result.lead,
                    leads_list_ids: [...new Set([...result.lead.leads_list_ids, list.id])],
                  },
                }
              : result,
          ),
        )
      } else {
        App.displayFlash("error", "Failed to save lead to a list")
      }
    }
  }

  const handleLeadManagerSave = async (name: string, listId: number) => {
    const listAlreadyExists = leadsLists.find((list) => list.id === listId)

    if (!listAlreadyExists) {
      setLeadsLists((prev) => [...prev, { id: listId, name }])
    }

    // Reveal emails with the specified list
    const idsToReveal = selectedIds.length > 0 ? selectedIds : filteredEmails.map((e) => e.id)

    await handleRevealMultiple(idsToReveal, { leads_list_id: listId })
  }

  // Calculate matching keywords for each lookalike
  const matchingKeywords = React.useMemo(() => {
    if (sourceKeywords.length === 0) return {}

    const matches: Record<number, string[]> = {}
    Object.keys(lookalikeKeywords).forEach((emailIdStr) => {
      const emailId = parseInt(emailIdStr)
      const keywords = lookalikeKeywords[emailIdStr] || []
      const common = sourceKeywords.filter((sk) => keywords.includes(sk))
      if (common.length > 0) {
        matches[emailId] = common
      }
    })
    return matches
  }, [sourceKeywords, lookalikeKeywords])

  // Extract unique job titles from emails
  const availableJobTitles = React.useMemo(() => {
    return Array.from(new Set(emails.map((email) => email.jobTitle).filter(Boolean))).sort()
  }, [emails])

  // Memoize unrevealed counts to avoid repeated filtering in render
  const unrevealedFilteredCount = React.useMemo(() => {
    return filteredEmails.filter((e) => !e.revealed).length
  }, [filteredEmails])

  const unrevealedSelectedCount = React.useMemo(() => {
    if (selectedIds.length === 0) return 0
    return selectedIds.filter((id) => !emails.find((e) => e.id === id)?.revealed).length
  }, [selectedIds, emails])

  // Memoize single intent alert to prevent re-rendering on scroll
  const singleIntentAlert = React.useMemo(() => {
    if (embeddedMode || intents.length !== 1) return null

    const intent = intents[0]
    const position = intent.metadata?.position
    const industry = intent.metadata?.industry
    const country = intent.metadata?.country

    // Build dynamic message based on available metadata
    let message = "These leads are similar to your selection"

    if (position && industry && country) {
      message = `These leads are other <strong class='fw-medium'>${position}</strong> in <strong class='fw-medium'>${industry}</strong> companies in <strong class='fw-medium'>${country.toUpperCase()}</strong>`
    } else if (position && industry) {
      message = `These leads are other <strong class='fw-medium'>${position}</strong> in <strong class='fw-medium'>${industry}</strong> companies`
    } else if (industry && country) {
      message = `These leads are other professionals in <strong class='fw-medium'>${industry}</strong> companies in <strong class='fw-medium'>${country.toUpperCase()}</strong>`
    } else if (position) {
      message = `These leads are other <strong class='fw-medium'>${position}</strong>`
    } else if (industry) {
      message = `These leads are other professionals in <strong class='fw-medium'>${industry}</strong> companies`
    }

    message += "."

    return (
      <Alert className="sidepanel__alert h-alert--ai">
        <AlertIcon>
          <Icon icon="sparkles" />
        </AlertIcon>
        <AlertContent>
          <AlertDescription>
            <p className="mb-0" dangerouslySetInnerHTML={{ __html: message }} />
          </AlertDescription>
        </AlertContent>
      </Alert>
    )
  }, [embeddedMode, intents])

  if (isLoading && emailIds.length === 0) {
    return (
      <div className="lead-lookalikes__loading-container">
        <LoadingShimmer
          texts={[
            i18n.t("js.lead_lookalikes.loading.analyzing"),
            i18n.t("js.lead_lookalikes.loading.grouping"),
            i18n.t("js.lead_lookalikes.loading.finding"),
          ]}
        />
      </div>
    )
  }

  return (
    <>
      {!embeddedMode && emailIds.length > 0 && (
        <header className="sidepanel__header mb-0">
          <h2 className="sidepanel__title">
            {i18n.t("js.lead_lookalikes.header_title", {
              count: emails.length > 0 ? filteredEmails.length : emailIds.length,
            })}
          </h2>
        </header>
      )}

      {embeddedMode && emailIds.length > 0 && leadName && (
        <div
          className="text-sm mb-2"
          dangerouslySetInnerHTML={{
            __html: i18n.t("js.lead_lookalikes.embedded_description_html", {
              count: emails.length > 0 ? filteredEmails.length : emailIds.length,
              results: CurrencyHelper.numberWithDelimiter(emails.length > 0 ? filteredEmails.length : emailIds.length),
              lead_name: leadName,
            }),
          }}
        />
      )}

      {/* Show empty state when no lookalikes are found */}
      {emailIds.length === 0 && !isLoading && (
        <LeadLookalikesEmptyState
          errorTitle={error?.title || i18n.t("js.lead_lookalikes.errors.generic.title")}
          errorDescription={error?.description || i18n.t("js.lead_lookalikes.errors.generic.description")}
          embeddedMode={embeddedMode}
          onClose={onClose}
        />
      )}

      {!embeddedMode && intents.length > 1 && (
        <Alert variant="info" className="sidepanel__alert">
          <AlertIcon>
            <Icon icon="users" />
          </AlertIcon>
          <AlertContent>
            <AlertDescription>
              <p className="mb-0">
                We found {intents.length} different personas in your selection. Pick a segment below to refine results.
              </p>
              <ul>
                <li>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      setSelectedIntentIndex(null)
                    }}
                    style={{
                      fontWeight: selectedIntentIndex === null ? "bold" : "normal",
                    }}
                  >
                    Show all personas
                  </a>
                </li>
                {intents.map((intent, index) => {
                  const parts = []

                  // Add position if available
                  if (intent.metadata?.position) {
                    parts.push(intent.metadata.position)
                  }

                  // Add industry if available
                  if (intent.metadata?.industry) {
                    parts.push(intent.metadata.industry)
                  }

                  // Add country if available (format nicely)
                  if (intent.metadata?.country) {
                    const countryCode = intent.metadata.country.toUpperCase()
                    parts.push(countryCode)
                  }

                  const displayText = parts.length > 0 ? parts.join(" · ") : `Segment ${index + 1}`

                  return (
                    <li key={index}>
                      {displayText} →{" "}
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault()
                          setSelectedIntentIndex(index)
                        }}
                        style={{
                          fontWeight: selectedIntentIndex === index ? "bold" : "normal",
                        }}
                      >
                        Use this segment
                      </a>
                    </li>
                  )
                })}
              </ul>
            </AlertDescription>
          </AlertContent>
        </Alert>
      )}

      {singleIntentAlert}

      {/* Filters - only show when we have results and no error */}
      {emailIds.length > 0 && !error && (
        <LookalikesFilters
          filters={filters}
          setFilters={setFilters}
          jobTitles={availableJobTitles}
          clearFilters={clearFilters}
          anyFiltersSet={anyFiltersSet}
          sourceKeywords={sourceKeywords}
          selectedKeywords={selectedKeywords}
          setSelectedKeywords={setSelectedKeywords}
        />
      )}

      {/* Results - only show when we have data */}
      {emailIds.length > 0 && !error && (
        <>
          <div
            ref={parentRef}
            className={embeddedMode ? "mt-4" : "sidepanel__body pb-12 mt-4"}
            style={
              embeddedMode ? { maxHeight: "600px", overflow: "auto" } : { height: contentHeight, overflow: "auto" }
            }
          >
            <VirtualizedResults
              virtualItems={virtualizer.getVirtualItems()}
              getTotalSize={() => virtualizer.getTotalSize()}
              measureElement={virtualizer.measureElement}
              results={filteredEmails}
              domains={domains}
              selectedIds={selectedIds}
              leadsLists={leadsLists}
              onSelect={handleSelect}
              onReveal={handleReveal}
              onSaveEmail={handleLeadsListSave}
              similarityThresholds={similarityThresholds}
              matchingKeywords={matchingKeywords}
            />
            {isLoading && emailIds.length > 0 && (
              <div className="d-flex justify-content-center p-4">
                <Icon icon="spinner-third" className="fa-spin" aria-hidden="true" />
              </div>
            )}
          </div>

          {/* Footer */}
          <footer
            className={
              embeddedMode
                ? "sidepanel__footer d-flex align-items-center justify-content-between gap-3"
                : "sidepanel__footer sidepanel__footer--sticky"
            }
          >
            {/* Snackbar notification - positioned inside footer for proper CSS positioning */}
            {revealedIds.length > 0 && (
              <Snackbar>
                <Icon icon="check-circle" variant="solid" size="sm" className="text-success" />
                <SnackbarText>
                  <strong className="fw-medium">
                    {i18n.t("js.multi_domain_search.footer.leads", {
                      count: revealedIds.length,
                      pretty_count: CurrencyHelper.numberWithDelimiter(revealedIds.length),
                    })}
                  </strong>
                  &nbsp;
                  {i18n.t("js.multi_domain_search.footer.saved")}
                </SnackbarText>
                <SnackbarLink
                  href={leads_path({
                    updated_at: "last1h",
                  })}
                >
                  {i18n.t("js.multi_domain_search.footer.see_in_leads")}
                </SnackbarLink>
              </Snackbar>
            )}

            <ButtonGroup>
              <Button
                variant="primary"
                onClick={() => {
                  const idsToReveal = selectedIds.length > 0 ? selectedIds : filteredEmails.map((e) => e.id)
                  handleRevealMultiple(idsToReveal)
                }}
                disabled={
                  revealingIds.length > 0 ||
                  (selectedIds.length > 0 ? unrevealedSelectedCount === 0 : unrevealedFilteredCount === 0)
                }
              >
                {revealingIds.length > 0
                  ? i18n.t("js.lead_lookalikes.revealing")
                  : selectedIds.length > 0
                    ? i18n.t("js.lead_lookalikes.reveal_selected", {
                        count: selectedIds.length,
                      })
                    : i18n.t("js.lead_lookalikes.reveal_all")}
                <ButtonSuffix>
                  <span className="cost-indicator cost-indicator--bare">
                    <Icon icon="coin" size="xs" className="cost-indicator__icon" />
                    {selectedIds.length > 0 ? unrevealedSelectedCount : unrevealedFilteredCount}
                  </span>
                </ButtonSuffix>
              </Button>

              <LeadSaveManager
                id="lead-lookalikes"
                leadsLists={leadsLists}
                onSave={handleLeadManagerSave}
                disableButtons={revealingIds.length > 0}
                cost={selectedIds.length > 0 ? unrevealedSelectedCount : unrevealedFilteredCount}
              >
                <Button
                  variant="primary"
                  disabled={revealingIds.length > 0}
                  iconOnly
                  data-bs-auto-close="outside"
                  data-bs-toggle="dropdown"
                  title=""
                  type="button"
                  value=""
                >
                  <Icon icon="angle-down" />
                </Button>
              </LeadSaveManager>
            </ButtonGroup>

            {/* Credits Usage */}
            <div className="d-flex align-items-center gap-2 text-size-sm">
              {i18n.t("js.lead_lookalikes.credits_usage")}
              <Tag>
                <TagIcon>
                  <Icon icon="coin" />
                </TagIcon>
                <TagLabel>
                  {i18n.t("js.lead_lookalikes.credits_text", {
                    count: selectedIds.length > 0 ? unrevealedSelectedCount : unrevealedFilteredCount,
                  })}
                </TagLabel>
              </Tag>
            </div>
          </footer>
        </>
      )}
    </>
  )
}

export default LeadLookalikes
